import React from 'react';
import { Col, Form, Input, Row, Space, Switch } from 'antd';
import type { FormInstance, Rule } from 'antd/es/form';
import { EIQFormLabel } from 'common/components/label/form-label';
import { ICitationConfigurationFormValues } from './citation-configuration-form.types';

interface CitationGeneralSettingsProps {
  form: FormInstance<ICitationConfigurationFormValues>;
  showLegacyToleranceFields: boolean;
}

const REQUIRED_DAY_RULES: Record<string, Rule[]> = {
  unassignedCitationRemoveDelayInDays: [{ required: true, message: 'Unassigned citation removal delay is required' }],
  canceledCitationRemoveDelayInDays: [{ required: true, message: 'Canceled citation removal delay is required' }],
  delinquentCitationInDays: [{ required: true, message: 'Delinquent payment delay is required' }],
  citationResetInDays: [{ required: true, message: 'Citation reset delay is required' }],
};

const CitationGeneralSettings = ({ form, showLegacyToleranceFields }: CitationGeneralSettingsProps) => {
  const isAutoIssueInvoice = Boolean(Form.useWatch('isAutoIssueInvoice', form));
  const warningToleranceEnabled = Boolean(Form.useWatch('isFirstCitationWarningToleranceInMPHEnabled', form));
  const escalationToleranceEnabled = Boolean(Form.useWatch('isFirstCitationEscalationToleranseInMPHEnabled', form));

  return (
    <>
      <Row>
        <Col xs={24} sm={12}>
          <BooleanSetting
            name="isPreventVendorCitations"
            title="Ignore vendor violations"
            description="When enabled, residents won’t be cited for their vendors' traffic violations."
          />
          <BooleanSetting
            name="isAutoIssueInvoice"
            title="Send online payment links"
            description="Violation notifications will include only fine information, they will not have any online payment links. Enable this option if you have already completed payment integration with EntranceIQ."
          />
          <BooleanSetting
            name="isAutoSuspendTransponders"
            title="Auto suspend transponders"
            description="Violation notifications will include only transponder suspension information, they will not automatically suspend transponders."
          />
        </Col>

        <Col xs={24} sm={12}>
          <DaySetting
            name="unassignedCitationRemoveDelayInDays"
            title="Unassigned citation removal (days)"
            description="Days an unassigned citation remains in a citation list before being removed"
          />
          <DaySetting
            name="canceledCitationRemoveDelayInDays"
            title="Canceled citation removal (days)"
            description="Days a canceled citation remains in a citation list before being removed"
          />
          <DaySetting
            name="citationResetInDays"
            title="Citation reset (days)"
            description="Violations reset after the days set here unless there is a new offence. Counts all violations unless canceled."
          />
          <DaySetting
            name="delinquentCitationInDays"
            title="Delinquent payments (days)"
            description="Suspend all transponders of the resident for unpaid citations after the days set here."
            disabled={!isAutoIssueInvoice}
            required={isAutoIssueInvoice}
          />

          {showLegacyToleranceFields && (
            <>
              <ToleranceSetting
                enabledName="isFirstCitationWarningToleranceInMPHEnabled"
                valueName="firstCitationWarningToleranceInMPH"
                title="First Citation Warning Tolerance (mph)"
                description="The first offence is a warning unless this speed is exceeded; then penalties apply."
                enabled={warningToleranceEnabled}
              />
              <ToleranceSetting
                enabledName="isFirstCitationEscalationToleranseInMPHEnabled"
                valueName="firstCitationEscalationToleranseInMPH"
                title="First Citation Escalation Tolerance (mph)"
                description="If the recorded speed exceeds the set tolerance, the first offense is escalated to a second-level citation."
                enabled={escalationToleranceEnabled}
              />
            </>
          )}
        </Col>
      </Row>

      <Row>
        <Col xs={24}>
          <Space className="full-width">
            <Form.Item
              label={<EIQFormLabel title="General Information" description="" />}
              name="generalInformation"
              rules={[{ max: 2000, message: 'Maximum length is 2000 characters' }]}>
              <Input.TextArea rows={5} maxLength={2000} />
            </Form.Item>
          </Space>
        </Col>
      </Row>
    </>
  );
};

interface BooleanSettingProps {
  name: 'isPreventVendorCitations' | 'isAutoIssueInvoice' | 'isAutoSuspendTransponders';
  title: string;
  description: string;
}

const BooleanSetting = ({ name, title, description }: BooleanSettingProps) => (
  <Space className="full-width">
    <Form.Item name={name} label={<EIQFormLabel title={title} description={description} />} valuePropName="checked">
      <Switch />
    </Form.Item>
  </Space>
);

interface DaySettingProps {
  name: 'unassignedCitationRemoveDelayInDays' | 'canceledCitationRemoveDelayInDays' | 'citationResetInDays' | 'delinquentCitationInDays';
  title: string;
  description: string;
  disabled?: boolean;
  required?: boolean;
}

const DaySetting = ({ name, title, description, disabled = false, required = true }: DaySettingProps) => (
  <Space className="full-width citation-setting-column">
    <Form.Item
      rules={required ? REQUIRED_DAY_RULES[name] : undefined}
      name={name}
      label={<EIQFormLabel title={title} description={description} />}>
      <Input type="number" min={0} max={365} step={1} disabled={disabled} />
    </Form.Item>
  </Space>
);

interface ToleranceSettingProps {
  enabledName: 'isFirstCitationWarningToleranceInMPHEnabled' | 'isFirstCitationEscalationToleranseInMPHEnabled';
  valueName: 'firstCitationWarningToleranceInMPH' | 'firstCitationEscalationToleranseInMPH';
  title: string;
  description: string;
  enabled: boolean;
}

const ToleranceSetting = ({ enabledName, valueName, title, description, enabled }: ToleranceSettingProps) => (
  <Space className="full-width citation-setting-column">
    <div className="resident-section-fields-item">
      <Form.Item name={enabledName} valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name={valueName} label={<EIQFormLabel title={title} description={description} />}>
        <Input type="number" min={0} max={300} step={1} disabled={!enabled} />
      </Form.Item>
    </div>
  </Space>
);

export default CitationGeneralSettings;
